import React from 'react';
import cx from 'classnames';
import { withStyles, Theme, WithStyles, createStyles } from '@material-ui/core';

import { withIntl, IntlProps } from 'ha/i18n';
import { Button, Container, Grid, Typography, units } from '@ha/dsl';
import Img from 'ha/ui/next/Img';
import LazyComponent from 'ha/components/LazyComponent';

import bgTenant from 'img-src/home/testimonial-tenant.jpg';
import bgLandlord from 'img-src/home/testimonial-landlord.jpg';
import { tenantTestimonial, landlordTestimonial } from '../constants';

export const styles = (theme: Theme) =>
  createStyles({
    container: {
      position: 'relative',
      backgroundColor: 'black',
      backgroundSize: 'cover',
      padding: units.txb(4, 1.125, 3),
      height: 'auto',
      overflow: 'hidden',
      [theme.breakpoints.up('md')]: {
        padding: units.txb(11.25, 4, 9.75),
      },
    },
    imageWrapper: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: '100%',
      width: '100%',
      objectFit: 'cover',
    },
    tenantImgage: {
      objectPosition: 'right',
      [theme.breakpoints.up('md')]: {
        objectPosition: 'center',
      },
    },
    landlordImgage: {
      objectPosition: '30%',
      [theme.breakpoints.up('md')]: {
        objectPosition: 'center',
      },
    },
    content: {
      position: 'relative',
      zIndex: 1,
    },
    shadowForContent: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      position: 'absolute',
      background:
        'linear-gradient(to right, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0))',
    },
    title: {
      fontSize: units.rem(1.5),
      fontStyle: 'italic',
      lineHeight: 1.29,
      marginBottom: units.rem(0.5),
      [theme.breakpoints.up('md')]: {
        lineHeight: 1.23,
        fontSize: units.rem(2.5),
        marginBottom: units.rem(1),
      },
    },
    text: {
      fontSize: units.rem(1.125),
      lineHeight: 1.6,
      paddingRight: units.rem(3.875),
      marginBottom: units.rem(0.75),
      [theme.breakpoints.up('md')]: {
        fontSize: units.rem(1.25),
        lineHeight: 1.9,
        paddingRight: units.rem(7.875),
        marginBottom: units.rem(1.25),
      },
    },
    button: {
      color: theme.palette.white.main,
      background: 'linear-gradient(to right, #b56da8, #8e5082)',
      boxShadow: 'inherit',
      '&:hover': {
        boxShadow: '0 2px 9px 0 rgba(146, 83, 134, 0.35)',
      },
      height: units.rem(3.5),
      padding: units.rem(1.125),
      fontWeight: 'bold',
      [theme.breakpoints.up('md')]: {
        padding: units.yx(1.125, 1.75),
      },
    },
  });

interface Props {
  isTenant?: boolean;
  isPricing?: boolean;
  ctaUrl?: string;
}

export const Testimonial: React.FC<
  Props & IntlProps & WithStyles<typeof styles>
> = ({ classes, isTenant, ctaUrl, intl: { T } }) => {
  const testimonial = isTenant ? tenantTestimonial : landlordTestimonial;
  const backgroundImage = isTenant ? bgTenant : bgLandlord;
  const imageClass = isTenant ? classes.tenantImgage : classes.landlordImgage;

  return (
    <div className={classes.container}>
      <LazyComponent bottomOffset="-200px" scrollableAncestor="window">
        <Img
          src={backgroundImage}
          className={cx(classes.imageWrapper, imageClass)}
          role="presentation"
        />
      </LazyComponent>
      <Container className={classes.content}>
        <Grid container direction="column" justify="center">
          <Grid
            item
            container
            xs={12}
            sm={6}
            direction="column"
            justify="center"
            alignItems="flex-start"
          >
            <Typography.H3
              className={classes.title}
              data-test-locator="Home Testimonial Title"
              component="h3"
              color="white"
              align="left"
            >
              {T(testimonial.title)}
            </Typography.H3>
            <Typography.Paragraph
              component="p"
              className={classes.text}
              color="white"
              align="left"
              data-test-locator="Home Testimonial Text"
            >
              {T(testimonial.text)}
            </Typography.Paragraph>
            <Button
              variant="contained"
              href={ctaUrl}
              classes={{ root: classes.button }}
              onClick={event => {
                if (!ctaUrl) {
                  event.preventDefault();
                  window.scrollTo(0, 0);
                }
              }}
              data-test-locator="Home Testimonial CTA"
            >
              {T(testimonial.ctaText)}
            </Button>
          </Grid>
        </Grid>
      </Container>
      <div className={classes.shadowForContent} />
    </div>
  );
};

export default withStyles(styles)(withIntl(Testimonial));
